import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { KpiCard } from '../components/KpiCard'
import { Card, CardHeader } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { reportesApi } from '../api/reportes'
import { cargasApi, type Carga } from '../api/cargas'
import { fixed, toNum, num, bs, litros as fmtLitros } from '../utils/format'

// ---- date helpers ----

function todayRange(): { desde: string; hasta: string } {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
  const end   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  return {
    desde: start.toISOString(),
    hasta: end.toISOString(),
  }
}

function lastNDaysRange(n: number): { desde: string; hasta: string } {
  const end   = new Date()
  const start = new Date()
  start.setDate(start.getDate() - (n - 1))
  start.setHours(0, 0, 0, 0)
  return {
    desde: start.toISOString(),
    hasta: end.toISOString(),
  }
}

type Preset = 'today' | '7d' | '30d'

function presetToRange(p: Preset): { desde: string; hasta: string } {
  if (p === 'today') return todayRange()
  if (p === '7d')   return lastNDaysRange(7)
  return lastNDaysRange(30)
}

function presetLabel(p: Preset): string {
  if (p === 'today') return 'Hoy'
  if (p === '7d')    return '7 días'
  return '30 días'
}

// ---- skeleton ----

function SkeletonKPI() {
  return (
    <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-2">
      <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      <div className="h-8 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
    </div>
  )
}

// ---- FlotaCostKpi (weighted avg Bs/h from ranking-vehiculos) ----

function FlotaCostKpi({ desde, hasta }: { desde: string; hasta: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ranking-vehiculos', desde, hasta],
    queryFn: () => reportesApi.rankingVehiculos(desde, hasta),
    staleTime: 60_000,
  })

  let display = '—'
  let color: 'green' | 'red' | 'orange' = 'orange'

  if (data?.items && data.items.length > 0) {
    const totalHoras   = data.items.reduce((s, i) => s + toNum(i.horasTrabajadas), 0)
    const weightedSum  = data.items.reduce(
      (s, i) => s + toNum(i.costoPorHoraBs) * toNum(i.horasTrabajadas),
      0,
    )
    const avg   = totalHoras > 0 ? weightedSum / totalHoras : 0
    display     = `Bs. ${fixed(avg, 1)}`
    const avgVs = data.items.reduce((s, i) => s + toNum(i.vsEsperadoPct), 0) / data.items.length
    color       = avgVs > 20 ? 'red' : avgVs > 0 ? 'orange' : 'green'
  }

  if (isError) display = 'Error'

  if (isLoading) return <SkeletonKPI />

  return (
    <KpiCard
      title="Costo Bs/h flota"
      value={display}
      subtitle="promedio ponderado"
      icon={<span className="text-lg">⚙</span>}
      color={color}
    />
  )
}

// ---- main ----

export default function Dashboard() {
  const [preset, setPreset] = useState<Preset>('today')

  // Tick granularity: minuto. Sin esto, presetToRange() crea Date() en cada render,
  // cambia el queryKey de react-query y dispara un loop infinito de fetch.
  const [minuteTick, setMinuteTick] = useState(() => Math.floor(Date.now() / 60_000))
  useEffect(() => {
    const id = setInterval(() => setMinuteTick(Math.floor(Date.now() / 60_000)), 30_000)
    return () => clearInterval(id)
  }, [])

  const { desde, hasta } = useMemo(() => presetToRange(preset), [preset, minuteTick])

  // Single query covering KPIs: total, litros, monto, outliers
  const { data: cargasData, isLoading: kpiLoading, isError: kpiError, refetch: kpiRefetch } = useQuery({
    queryKey: ['dashboard-cargas', desde, hasta],
    queryFn: () => cargasApi.list({ desde, hasta, page: 1, pageSize: 200 }),
    staleTime: 60_000,
  })

  // Ranking operadores del mes actual — fuente del Top 3 outliers
  const { data: rankingData, isLoading: rankingLoading } = useQuery({
    queryKey: ['dashboard-ranking-operadores'],
    queryFn: () => reportesApi.getRankingOperadores(),
    staleTime: 60_000,
  })

  // Derived KPI values
  const items       = cargasData?.items ?? []
  const totalCargas = cargasData?.total ?? 0
  const totalLitros = items.reduce((s, c) => s + toNum(c.litros), 0)
  const totalMonto  = items.reduce((s, c) => s + toNum(c.montoTotal), 0)
  const totalOutliers = items.filter((c) => c.estadoValidacion === 'OUTLIER').length
  const outlierPct  = totalCargas > 0 ? (totalOutliers / totalCargas) * 100 : 0
  const vehiculosActivos = new Set(items.map((c) => c.vehiculoId)).size

  // Top 3 operadores por outliers del mes (filtra los que tienen al menos 1)
  const topOutliers = (rankingData?.items ?? [])
    .filter((r) => r.outliers > 0)
    .slice(0, 3)

  // Recent 6, sorted creadoAt desc
  const recentCargas: Carga[] = [...items]
    .sort((a, b) => new Date(b.creadoAt).getTime() - new Date(a.creadoAt).getTime())
    .slice(0, 6)

  const subtitlePeriod = preset === 'today' ? 'hoy' : preset === '7d' ? 'últimos 7 días' : 'últimos 30 días'

  return (
    <div className="space-y-6">
      {/* Header + period selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {new Date().toLocaleDateString('es-BO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          {(['today', '7d', '30d'] as Preset[]).map((p) => (
            <button
              key={p}
              onClick={() => setPreset(p)}
              className={[
                'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                preset === p
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200',
              ].join(' ')}
            >
              {presetLabel(p)}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      {kpiLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonKPI key={i} />)}
        </div>
      ) : kpiError ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center justify-between">
          <p className="text-sm text-red-700 dark:text-red-300">Error al cargar KPIs</p>
          <Button variant="secondary" size="sm" onClick={() => kpiRefetch()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
          <KpiCard
            title="Cargas"
            value={totalCargas}
            subtitle={subtitlePeriod}
            icon={<span className="text-lg">⛽</span>}
            color="blue"
          />
          <KpiCard
            title="Litros"
            value={fmtLitros(totalLitros, 0)}
            subtitle={subtitlePeriod}
            icon={<span className="text-lg">🛢</span>}
            color="purple"
          />
          <KpiCard
            title="Monto total"
            value={bs(totalMonto, 0)}
            subtitle="en BOB"
            icon={<span className="text-lg">💰</span>}
            color="green"
          />
          <KpiCard
            title="Outliers"
            value={totalOutliers}
            subtitle={totalCargas > 0 ? `${fixed(outlierPct, 1)}% del total` : 'requieren revisión'}
            icon={<span className="text-lg">⚠</span>}
            color={totalOutliers > 0 ? 'red' : 'green'}
          />
          <KpiCard
            title="Vehículos activos"
            value={vehiculosActivos}
            subtitle={`con cargas ${subtitlePeriod}`}
            icon={<span className="text-lg">🚜</span>}
            color="blue"
          />
          <FlotaCostKpi desde={desde} hasta={hasta} />
        </div>
      )}

      {/* Top 3 operadores con más outliers (mes actual) */}
      <Card>
        <CardHeader
          title="Top operadores con más outliers"
          subtitle="Mes en curso · ordenado por % de outliers sobre sus cargas"
          action={
            <Link
              to="/reportes"
              className="text-xs font-medium text-primary hover:text-primary-dark"
            >
              Ver ranking completo →
            </Link>
          }
        />
        {rankingLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-12 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse" />
            ))}
          </div>
        ) : topOutliers.length === 0 ? (
          <div className="text-center py-6 text-slate-400 dark:text-slate-500">
            <p className="text-3xl mb-1">✅</p>
            <p className="text-sm">Sin operadores con outliers este mes</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topOutliers.map((op) => (
              <div
                key={op.operadorId}
                className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/60"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 text-xs font-bold shrink-0">
                    {op.posicion}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                      {op.nombre}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {op.cargas} cargas · {fmtLitros(toNum(op.litros), 0)}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-red-600 dark:text-red-300 tabular-nums">
                    {fixed(toNum(op.porcentajeOutlier), 2)}%
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                    {op.outliers} {op.outliers === 1 ? 'outlier' : 'outliers'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent cargas table */}
      <Card>
        <CardHeader
          title="Cargas recientes"
          subtitle={`Últimas 6 cargas · ${subtitlePeriod}`}
        />
        {kpiLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-9 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse" />
            ))}
          </div>
        ) : recentCargas.length === 0 ? (
          <div className="text-center py-10 text-slate-400 dark:text-slate-500">
            <p className="text-4xl mb-2">⛽</p>
            <p className="text-sm">Sin cargas en el período seleccionado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700">
                  {['Hora', 'Vehículo', 'Operador', 'Litros', 'Estado'].map((h) => (
                    <th
                      key={h}
                      className="text-left px-2 py-2 text-xs text-slate-400 font-semibold uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                {recentCargas.map((row) => {
                  const hora = new Date(row.creadoAt).toLocaleTimeString('es-BO', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                  const vehiculoCodigo = row.vehiculo?.codigo ?? row.vehiculoId.slice(0, 8)
                  const operadorNombre = row.operador?.nombre ?? row.operadorId.slice(0, 8)
                  return (
                    <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                      <td className="px-2 py-2.5 text-slate-500 tabular-nums">{hora}</td>
                      <td className="px-2 py-2.5 font-medium text-slate-800 dark:text-slate-200">
                        {vehiculoCodigo}
                      </td>
                      <td className="px-2 py-2.5 text-slate-600 dark:text-slate-400">
                        {operadorNombre}
                      </td>
                      <td className="px-2 py-2.5 text-slate-700 dark:text-slate-300 tabular-nums">
                        {num(row.litros, 0)} L
                      </td>
                      <td className="px-2 py-2.5">
                        <Badge estado={row.estadoValidacion} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
